import { createContext, useContext, type ReactNode } from 'react';
import { CookieConsent } from '@acast-tech/decibel';
import { useCookieConsent as useAppShellCookieConsent } from '@acast-tech/product-suite-app-shell';
import { config } from '@/config';

/**
 * Local cookie consent context for the Echo nav path where AppShell is not mounted.
 *
 * AppShell's `useCookieConsent` reads from a private context that only exists
 * inside `<AppShell>`. In the Echo nav layout, AppShell is replaced by
 * `<AppConfig>` + `<CreatorHubNav>`, so that context is never provided and
 * the hook silently returns `false`, preventing Mixpanel from initialising.
 *
 * This module provides a `CookieConsentProvider` (wrapping Decibel's
 * `<CookieConsent>` render-prop) and a combined `useCookieConsent` hook that
 * returns `true` when *either* provider has consent — so it works in both the
 * AppShell and Echo nav paths.
 */

const LocalCookieConsentContext = createContext(false);

interface CookieConsentProviderProps {
  children: ReactNode;
}

/**
 * Renders Decibel's `<CookieConsent>` banner and provides the consent value
 * via a local React context. Mount this inside layouts that don't use AppShell.
 */
export const CookieConsentProvider = ({ children }: CookieConsentProviderProps): JSX.Element => (
  <CookieConsent policyUrl={config.cookiePolicyUrl} zIndex={2147483638}>
    {({ hasConsent }) => (
      <LocalCookieConsentContext.Provider value={hasConsent}>
        {children}
      </LocalCookieConsentContext.Provider>
    )}
  </CookieConsent>
);

/**
 * Returns `true` when the user has given cookie consent.
 *
 * Works in both layout paths:
 * - **AppShell path:** AppShell's private `CookieConsentContext` provides the
 *   value; the local context defaults to `false`.
 * - **Echo nav path:** `CookieConsentProvider` provides the value via the
 *   local context; AppShell's hook defaults to `false`.
 */
export const useCookieConsent = (): boolean => {
  const localConsent = useContext(LocalCookieConsentContext);
  const appShellConsent = useAppShellCookieConsent();
  return localConsent || appShellConsent;
};
